// @ts-strict-ignore
import type { IconDefinition } from "@fortawesome/fontawesome-common-types";
import type { ReactNode } from "react";

import { Typography } from "@aviary/components/Typography";
import { useIsDS4Enabled } from "@aviary/hooks/useIsDS4Enabled";
import type { AviaryIllustration } from "@aviary/types";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";

import { Box } from "../Box";
import { CircularIcon } from "../CircularIcon";
import { Illustration } from "../Illustration/Illustration";

import * as styles from "./EmptyState.styles";

interface Props {
  /**
   * Adds in an icon contained with a circle above the title/content
   */
  icon?: IconDefinition;
  /**
   * Adds an Aviary Illustration
   */
  illustration?: AviaryIllustration;
  /**
   * Call to action header
   */
  header?: string;
  /**
   * Allows addition of a button to create actionable item
   */
  action?: ReactNode;
  /**
   * Adds a border to the component
   */
  isBorderless?: boolean;
  /**
   * Content to render inside the empty state
   *
   * @default undefined
   */
  children?: ReactNode;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/layout/EmptyState
 */
const EmptyState = ({
  header,
  icon,
  action,
  children,
  illustration,
  isBorderless = false,
  ...rest
}: Props) => {
  const isDS4Enabled = useIsDS4Enabled();
  const { phoneLarge } = useBreakpoints();
  const wrapperStyles = [styles.emptyState.base, isBorderless && styles.emptyState.borderless];

  const renderIcon = () => {
    if (icon) {
      return (
        <div css={styles.icon}>
          <CircularIcon isColor="system" size="small" icon={icon} />
        </div>
      );
    }

    if (illustration) {
      return (
        <Illustration
          illustration={illustration}
          css={styles.icon}
          isMobile={phoneLarge.lessThan}
        />
      );
    }
  };

  const renderHeader = () => {
    if (header) {
      return (
        <Typography type="h5" isMarginless css={styles.title}>
          {header}
        </Typography>
      );
    }
  };

  return (
    <Box
      layoutStyles={wrapperStyles}
      isBordered
      isColor={isDS4Enabled ? undefined : "system"}
      {...rest}
    >
      <div css={styles.emptyStateContent}>
        {renderIcon()}
        {renderHeader()}
        <Typography isMarginless={!action} css={styles.text}>
          {children}
        </Typography>
        <div css={styles.action}>{action}</div>
      </div>
    </Box>
  );
};

export { EmptyState };
